import {
  Select as ShadSelect,
  SelectContent,
  SelectTrigger,
  SelectValue,
  SelectItem,
} from '@consigli/shadcn';
import * as React from 'react';

interface SelectProps<T = string> {
  options: T[];
  value?: T;
  onValueChange?: (value: T) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  contentClassName?: string;
  getOptionLabel?: (option: T) => string;
  getOptionValue?: (option: T) => string;
  showSearch?: boolean;
}

export function Select<T = string>({
  options = [],
  value,
  onValueChange,
  placeholder = 'Select...',
  disabled = false,
  className = 'w-[180px] h-[40px] text-sm bg-white shadow-none',
  contentClassName,
  getOptionLabel = (option: T) => String(option),
  getOptionValue = (option: T) => String(option),
  showSearch = false,
}: SelectProps<T>) {
  const [searchTerm, setSearchTerm] = React.useState('');

  const filteredOptions = React.useMemo(() => {
    if (!showSearch || options.length <= 5) {
      return options;
    }
    return options.filter((option) =>
      getOptionLabel(option).toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [options, searchTerm, getOptionLabel, showSearch]);

  const handleValueChange = (newValue: string) => {
    const selectedOption = options.find((option) => getOptionValue(option) === newValue);
    if (selectedOption) {
      onValueChange?.(selectedOption);
    }
  };

  // Use empty string instead of undefined to keep the component controlled
  const currentValue = value ? getOptionValue(value) : '';

  return (
    <ShadSelect value={currentValue} onValueChange={handleValueChange} disabled={disabled}>
      <SelectTrigger className={className}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className={contentClassName}>
        {showSearch && options.length > 5 && (
          <div className="p-2 border-b">
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        )}
        {filteredOptions.map((option) => (
          <SelectItem
            key={getOptionValue(option)}
            value={getOptionValue(option)}
            className="truncate"
          >
            {getOptionLabel(option)}
          </SelectItem>
        ))}
      </SelectContent>
    </ShadSelect>
  );
}
